import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import type {
  InventoryItem,
  InventoryTransaction,
  PaginatedResponse,
} from "@/types";

interface StockAdjustment {
  quantity: number;
  reason: string;
  notes?: string;
}

interface InventoryFilters {
  search?: string;
  page?: number;
  limit?: number;
}

export function useInventory(params?: InventoryFilters) {
  return useQuery({
    queryKey: [...QUERY_KEYS.inventory, params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<InventoryItem>>("/inventory", {
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
      }),
    placeholderData: (prev) => prev,
  });
}

export const useIngredients = useInventory;

export function useInventoryItem(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.inventoryItem(id),
    queryFn: () => apiClient.get<InventoryItem>(`/inventory/${id}`),
    enabled: !!id,
  });
}

export function useStockMovements(params?: {
  ingredientId?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [...QUERY_KEYS.stockMovements(params?.ingredientId), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<InventoryTransaction>>(
        `/inventory/transactions`,
        {
          ingredientId: params?.ingredientId,
          search: params?.search,
          page: params?.page,
          limit: params?.limit,
        },
      ),
    placeholderData: (prev) => prev,
  });
}

export function useLowStockItems() {
  return useQuery({
    queryKey: QUERY_KEYS.lowStock,
    queryFn: () => apiClient.get<InventoryItem[]>("/inventory/low-stock"),
  });
}

export function useCreateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<InventoryItem>) =>
      apiClient.post<InventoryItem>("/inventory", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory });
    },
  });
}

export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InventoryItem> }) =>
      apiClient.put<InventoryItem>(`/inventory/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory });
    },
  });
}

export function useAdjustStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StockAdjustment }) =>
      apiClient.post<InventoryItem>(`/inventory/${id}/adjust`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stockMovements() });
    },
  });
}
